import { createFileRoute } from "@tanstack/react-router";
import { VoiceDarPage } from "@/components/landing/MissionReadyPublicSite";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD, softwareApplicationJsonLd, SITE_URL } from "@/lib/structured-data";

const TITLE = "Voice DAR™ — Voice Daily Activity Reports for Security Teams | MissionReady";
const DESCRIPTION =
  "Record daily activity reports by voice. MissionReady Voice DAR™ transcribes with Deepgram, summarizes, verifies, and preserves the original audio on a locked shift report.";

export const Route = createFileRoute("/voice-dar")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "voice DAR, daily activity report software, security guard voice reporting, DAR transcription, Deepgram transcription, shift report software",
      },
      ...socialMeta({
        title: "MissionReady Voice DAR™ — hands free, eyes stay observing",
        description:
          "Voice-first DAR reporting: Deepgram transcription, AI summaries, verified entries, retained audio, and chronological shift sheets.",
        url: `${SITE_URL}/voice-dar`,
      }),
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/voice-dar` }],
    scripts: [
      ...ORG_WEBSITE_JSONLD,
      softwareApplicationJsonLd({
        name: "MissionReady Voice DAR™",
        description: DESCRIPTION,
        url: `${SITE_URL}/voice-dar`,
        featureList: [
          "Voice-recorded daily activity reports (DAR)",
          "Speech-to-text transcription powered by Deepgram",
          "AI summaries of each recorded entry",
          "Guard verification with notes and categories",
          "Original audio retention with authorized playback",
          "Chronological D.A.R. shift sheet with locked submission",
          "Client-ready DAR exports",
        ],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Voice DAR", item: `${SITE_URL}/voice-dar` },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to file a daily activity report by voice with MissionReady Voice DAR",
          description:
            "Record, transcribe, verify, and submit a security daily activity report without stopping the shift.",
          step: [
            { "@type": "HowToStep", name: "Record", text: "Tap record and speak the observation while staying on post." },
            { "@type": "HowToStep", name: "Transcribe", text: "Deepgram transcription turns the recording into an accurate written entry." },
            { "@type": "HowToStep", name: "Verify", text: "The officer reviews the transcript, adds a note and category, and confirms." },
            { "@type": "HowToStep", name: "Submit", text: "The entry attaches to the open D.A.R. sheet, which is locked on end-of-shift submission." },
          ],
        }),
      },
    ],
  }),
  component: VoiceDarPage,
});
